// ─────────────────────────────────────────────────────────────────────────
// Heimdall dentro de la app (13-oct-2026) — panel de chat flotante, disponible
// en todas las pantallas autenticadas, con las conversaciones guardadas por
// usuario. Heimdall es el agente de operaciones del ERP (el mismo de Telegram);
// puede escribir en el ERP solo si el rol es admin u operator (lo decide el
// backend, acá no hay lógica de permisos).
// Backend: routes/heimdallChat.js · montado en /heimdall
// Montado en Main.js (botón + drawer) y en la ruta /heimdall (modo página).
// Colores: solo tokens CSS (el CI de colores no admite literales nuevos).
// ─────────────────────────────────────────────────────────────────────────
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import backend from "../backend";
import AuthContext from "../AuthContext";
import useIsMobile from "../hooks/useIsMobile";

// nginx corta a los 120 s: se corta antes para mostrar un mensaje propio.
const T = { timeout: 110000 };

const S = {
  input: {
    background: "var(--tab-campo)", border: "1px solid var(--tab-borde)", borderRadius: 8,
    padding: "10px 12px", fontSize: 14, color: "var(--tab-texto)", width: "100%",
    boxSizing: "border-box", outline: "none", resize: "none",
    fontFamily: "inherit", lineHeight: 1.4,
  },
  btn: {
    background: "var(--accent-color)", color: "var(--white-color)", border: "none", borderRadius: 8,
    padding: "10px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer",
  },
  btnGhost: {
    background: "transparent", color: "var(--tab-suave)", border: "1px solid var(--tab-borde)",
    borderRadius: 8, padding: "8px 12px", fontSize: 13, cursor: "pointer",
  },
};

const fmtFecha = v => {
  if (!v) return "";
  const d = new Date(v);
  const hora = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  if (d.toDateString() === new Date().toDateString()) return `hoy ${hora}`;
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()} ${hora}`;
};

// ── Markdown simple (sin dependencias): **negrita**, listas "- " y saltos ──
const conNegritas = (texto, clave) =>
  texto.split(/(\*\*[^*]+\*\*)/g).map((t, i) =>
    t.startsWith("**") && t.endsWith("**") && t.length > 4
      ? <strong key={`${clave}-${i}`}>{t.slice(2, -2)}</strong>
      : <React.Fragment key={`${clave}-${i}`}>{t}</React.Fragment>
  );

export const MarkdownSimple = ({ texto }) => {
  const bloques = [];
  let lista = null;
  String(texto || "").split("\n").forEach((linea, i) => {
    const item = linea.match(/^\s*[-*]\s+(.*)$/);
    if (item) {
      if (!lista) { lista = []; bloques.push({ tipo: "ul", items: lista, k: i }); }
      lista.push(item[1]);
    } else {
      lista = null;
      bloques.push({ tipo: "p", texto: linea, k: i });
    }
  });
  return (
    <div>
      {bloques.map(b =>
        b.tipo === "ul" ? (
          <ul key={b.k} style={{ margin: "4px 0", paddingLeft: 20 }}>
            {b.items.map((t, j) => <li key={j}>{conNegritas(t, `${b.k}-${j}`)}</li>)}
          </ul>
        ) : b.texto.trim() === "" ? (
          <div key={b.k} style={{ height: 8 }} />
        ) : (
          <div key={b.k}>{conNegritas(b.texto, b.k)}</div>
        )
      )}
    </div>
  );
};

// ── Contexto de pantalla: ruta + nombre del menú + números de OT/OC si vienen ──
const armarContexto = (pathname, search, menu) => {
  const seg = pathname.split("/")[1] || "";
  const nombre = (menu || []).find(i => i.path === seg)?.nombre || seg || "inicio";
  const partes = [`Pantalla: ${nombre} (${pathname})`];
  try {
    const q = new URLSearchParams(search);
    const claves = ["ot", "oc", "number", "numero", "wo", "po", "q", "buscar"];
    claves.forEach(k => {
      const v = q.get(k);
      if (v && v.length <= 30) partes.push(`${k}=${v}`);
    });
  } catch (e) { /* sin query */ }
  return partes.join(", ").slice(0, 200);
};

// ── Burbuja ───────────────────────────────────────────────────────────────
const descargar = async archivo => {
  try {
    const r = await backend.get(archivo.url, { responseType: "blob" });
    const href = window.URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = href;
    a.download = archivo.nombre;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(href);
  } catch (e) {
    window.alert("No se pudo descargar el archivo.");
  }
};

const Burbuja = ({ m }) => {
  const esUsuario = m.rol === "usuario";
  return (
    <div style={{ display: "flex", justifyContent: esUsuario ? "flex-end" : "flex-start" }}>
      <div
        style={{
          maxWidth: "88%", padding: "9px 13px", borderRadius: 12, fontSize: 14, lineHeight: 1.5,
          wordBreak: "break-word",
          ...(esUsuario
            ? { background: "var(--tab-boton)", color: "var(--tab-texto)", borderBottomRightRadius: 4, whiteSpace: "pre-wrap" }
            : { background: "var(--tab-caja)", color: "var(--tab-texto)", border: "1px solid var(--accent-color)", borderBottomLeftRadius: 4 }),
          ...(m.sinRespuesta ? { opacity: 0.75, border: "1px dashed var(--tab-suave)" } : {}),
        }}
      >
        {!esUsuario && (
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--accent-color)", marginBottom: 3 }}>Heimdall</div>
        )}
        {esUsuario ? m.texto : <MarkdownSimple texto={m.texto} />}
        {(m.archivos || []).map(a => (
          <div key={a.guardado || a.nombre} style={{ marginTop: 6 }}>
            <button type="button" onClick={() => descargar(a)} style={{ ...S.btnGhost, padding: "5px 10px", fontSize: 12 }}>
              Descargar {a.nombre}
            </button>
          </div>
        ))}
        <div style={{ fontSize: 10, color: "var(--tab-suave)", marginTop: 4, textAlign: esUsuario ? "right" : "left" }}>
          {fmtFecha(m.ts)}
        </div>
      </div>
    </div>
  );
};

// ── Chat completo: lista de conversaciones + conversación ─────────────────
export const HeimdallChat = ({ onClose, paginaCompleta = false }) => {
  const isMobile = useIsMobile();
  const auth = useContext(AuthContext);
  const location = useLocation();
  const [lista, setLista] = useState([]);
  const [conv, setConv] = useState(null);       // conversación abierta (con mensajes)
  const [verLista, setVerLista] = useState(false);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState("");
  const finRef = useRef(null);

  const cargarLista = async () => {
    try {
      const r = await backend.get("heimdall", T);
      setLista(r.data || []);
    } catch (e) { /* la lista es accesoria */ }
  };

  useEffect(() => { cargarLista(); }, []);
  useEffect(() => { finRef.current?.scrollIntoView({ block: "end" }); }, [conv, enviando]);

  const abrir = async id => {
    setAviso("");
    try {
      const r = await backend.get(`heimdall/${id}`, T);
      setConv(r.data);
      setVerLista(false);
    } catch (e) {
      setAviso(e.response?.data?.error || "No se pudo abrir la conversación.");
    }
  };

  const borrar = async c => {
    if (!window.confirm("¿Eliminar esta conversación?")) return;
    try {
      await backend.delete(`heimdall/${c._id}`, T);
      if (conv?._id === c._id) setConv(null);
      cargarLista();
    } catch (e) {
      setAviso(e.response?.data?.error || "No se pudo eliminar.");
    }
  };

  // Envía un mensaje (o reintenta si `reintento`). Muestra el mensaje del usuario
  // al tiro y deja que la respuesta del servidor reemplace la conversación.
  const enviar = async reintento => {
    const msg = reintento ? "" : texto.trim();
    if ((!msg && !reintento) || enviando) return;
    setAviso("");
    setEnviando(true);
    const contexto = armarContexto(location.pathname, location.search, auth?.descriptor?.menu);
    if (!reintento) {
      setTexto("");
      setConv(prev => ({
        ...(prev || { _id: null, mensajes: [] }),
        mensajes: [...(prev?.mensajes || []), { rol: "usuario", texto: msg, ts: new Date().toISOString() }],
      }));
    }
    try {
      const r = conv?._id
        ? await backend.post(`heimdall/${conv._id}/mensaje`, reintento ? {} : { mensaje: msg, contexto }, T)
        : await backend.post("heimdall", { mensaje: msg, contexto }, T);
      setConv(r.data);
      cargarLista();
    } catch (e) {
      const data = e.response?.data;
      setAviso(data?.error || "No se pudo contactar a Heimdall. Intente de nuevo.");
      const id = data?.chatId || conv?._id;
      if (id && e.response?.status === 502) {
        try { setConv((await backend.get(`heimdall/${id}`, T)).data); } catch (e2) { /* queda lo local */ }
        cargarLista();
      } else if (!e.response || e.response.status >= 500) {
        // Sin respuesta (timeout/corte) o error del servidor: la pregunta ya quedó guardada
        // antes de llamar al LLM. Si la conversación era nueva y no hay chatId, se recarga
        // la lista y se abre la más reciente.
        if (!conv?._id) {
          try {
            const filas = (await backend.get("heimdall", T)).data || [];
            setLista(filas);
            if (filas[0]) setConv((await backend.get(`heimdall/${filas[0]._id}`, T)).data);
          } catch (e2) { /* queda lo local */ }
        }
      } else if ([400, 403, 404, 409, 429].includes(e.response?.status)) {
        // El mensaje no se guardó (o hay otra respuesta en curso): se devuelve al
        // cuadro de texto para no perderlo.
        if (!reintento) {
          setTexto(msg);
          setConv(prev => (prev && prev._id ? { ...prev, mensajes: prev.mensajes.slice(0, -1) } : null));
        }
      }
    } finally {
      setEnviando(false);
    }
  };

  const onKey = e => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(false); }
  };

  const mensajes = conv?.mensajes || [];
  const ultimo = mensajes[mensajes.length - 1];
  const puedeReintentar = !enviando && ultimo && ultimo.rol === "usuario" && ultimo.sinRespuesta;
  const listaVisible = verLista;

  const encabezado = (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: "1px solid var(--tab-borde)", flexShrink: 0 }}>
      <div style={{ fontWeight: 800, fontSize: 15, color: "var(--tab-texto)", flex: 1 }}>
        <span style={{ color: "var(--accent-color)" }}>Heimdall</span>
        <span style={{ fontWeight: 400, fontSize: 12, color: "var(--tab-suave)", marginLeft: 8 }}>asistente del ERP</span>
      </div>
      <button type="button" style={S.btnGhost} onClick={() => setVerLista(v => !v)}>
        {listaVisible ? "Volver" : `Mis conversaciones${lista.length ? ` (${lista.length})` : ""}`}
      </button>
      <button type="button" style={{ ...S.btn, padding: "8px 12px", fontSize: 13 }} onClick={() => { setConv(null); setVerLista(false); setAviso(""); }}>
        Nueva
      </button>
      {onClose && (
        <button type="button" aria-label="Cerrar" style={{ ...S.btnGhost, padding: "6px 10px" }} onClick={onClose}>
          ✕
        </button>
      )}
    </div>
  );

  return (
    <div style={{
      display: "flex", flexDirection: "column", background: "var(--tab-fondo)", color: "var(--tab-texto)",
      height: paginaCompleta
        ? (isMobile ? "calc(100dvh - 130px - env(safe-area-inset-bottom))" : "calc(100vh - 24px)")
        : "100%",
      minHeight: 0,
      ...(paginaCompleta ? { maxWidth: 860, margin: isMobile ? 0 : "12px auto", border: "1px solid var(--tab-borde)", borderRadius: 12, overflow: "hidden" } : {}),
    }}>
      {encabezado}

      {listaVisible ? (
        <div style={{ flex: 1, overflowY: "auto", padding: 10 }}>
          {lista.length === 0 && <div style={{ color: "var(--tab-suave)", fontSize: 13, padding: 12 }}>Aún no tiene conversaciones.</div>}
          {lista.map(c => (
            <div key={c._id} style={{ display: "flex", gap: 6, alignItems: "stretch", marginBottom: 6 }}>
              <button
                type="button"
                onClick={() => abrir(c._id)}
                style={{
                  flex: 1, textAlign: "left", background: "var(--tab-caja)", color: "var(--tab-texto)",
                  border: `1px solid ${conv?._id === c._id ? "var(--accent-color)" : "var(--tab-borde)"}`,
                  borderRadius: 8, padding: "9px 12px", cursor: "pointer", minWidth: 0,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.titulo || "Conversación"}</div>
                <div style={{ fontSize: 11, color: "var(--tab-suave)" }}>{fmtFecha(c.updatedAt)}</div>
              </button>
              <button type="button" aria-label="Eliminar conversación" style={{ ...S.btnGhost, padding: "0 10px" }} onClick={() => borrar(c)}>🗑</button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          {mensajes.length === 0 && (
            <div style={{ color: "var(--tab-suave)", fontSize: 13, lineHeight: 1.5, padding: "16px 4px" }}>
              Pregúntele a Heimdall por órdenes de trabajo, piezas, producción, entregas o compras.
              Por ejemplo: «¿en qué estado está la OT 8438?».
            </div>
          )}
          {mensajes.map((m, i) => <Burbuja key={i} m={m} />)}
          {enviando && (
            <div style={{ color: "var(--tab-suave)", fontSize: 13, fontStyle: "italic" }}>Heimdall está revisando el ERP…</div>
          )}
          {puedeReintentar && (
            <div>
              <button type="button" style={S.btnGhost} onClick={() => enviar(true)}>Reintentar</button>
            </div>
          )}
          <div ref={finRef} />
        </div>
      )}

      {aviso && (
        <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--tab-ambar)", borderTop: "1px solid var(--tab-borde)", flexShrink: 0 }}>{aviso}</div>
      )}

      {!listaVisible && (
        <div style={{ display: "flex", gap: 8, padding: 10, borderTop: "1px solid var(--tab-borde)", alignItems: "flex-end", flexShrink: 0 }}>
          <textarea
            rows={2}
            style={S.input}
            value={texto}
            onChange={e => setTexto(e.target.value)}
            onKeyDown={onKey}
            placeholder="Escriba su consulta… (Enter envía, Shift+Enter salta de línea)"
            maxLength={4000}
          />
          <button type="button" style={{ ...S.btn, opacity: enviando || !texto.trim() ? 0.6 : 1 }} disabled={enviando || !texto.trim()} onClick={() => enviar(false)}>
            Enviar
          </button>
        </div>
      )}
    </div>
  );
};

// ── Modo página (ruta /heimdall) ──────────────────────────────────────────
export const HeimdallPagina = () => (
  <div style={{ padding: 0 }}>
    <HeimdallChat paginaCompleta />
  </div>
);

// ── Botón flotante + drawer derecho (montado en Main.js) ─────────────────
const HeimdallPanel = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [abierto, setAbierto] = useState(false);

  // En la página /heimdall el chat ya está a pantalla completa.
  if (location.pathname.startsWith("/heimdall")) return null;

  return (
    <>
      {!abierto && (
        <button
          type="button"
          aria-label="Abrir Heimdall"
          title="Heimdall, asistente del ERP"
          onClick={() => setAbierto(true)}
          style={{
            position: "fixed", right: 16, zIndex: 1060,
            // En móvil queda por encima de la barra inferior (52px + zona segura), no sobre ella.
            bottom: isMobile ? "calc(64px + env(safe-area-inset-bottom) + 12px)" : 20,
            width: 52, height: 52, borderRadius: "50%", border: "none", cursor: "pointer",
            background: "var(--accent-color)", color: "var(--white-color)",
            boxShadow: "var(--tab-sombra)", fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <i className="bi bi-chat-dots-fill" />
        </button>
      )}
      {/* Siempre montado (oculto al cerrar) para conservar la conversación abierta. */}
      {(
        <div style={{ display: abierto ? "block" : "none" }}>
          {!isMobile && (
            <div onClick={() => setAbierto(false)} style={{ position: "fixed", inset: 0, background: "var(--tab-velo)", zIndex: 1105 }} />
          )}
          <div
            role="dialog"
            aria-label="Heimdall"
            style={{
              position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 1110,
              width: isMobile ? "100%" : 440, maxWidth: "100%",
              boxShadow: "var(--tab-sombra)", paddingTop: isMobile ? "env(safe-area-inset-top)" : 0,
              paddingBottom: isMobile ? "env(safe-area-inset-bottom)" : 0, boxSizing: "border-box",
              background: "var(--tab-fondo)",
            }}
          >
            <HeimdallChat onClose={() => setAbierto(false)} />
          </div>
        </div>
      )}
    </>
  );
};

export default HeimdallPanel;
